import { MOTHERBOARD_PCB_THICKNESS } from '../spatial/geometry.js';
import { motherboardLandmarks } from './motherboard-landmarks.js?v=20260817-pcieanchor10';
import { defaultMotherboardRamSlotCount, normalizeMotherboardFormFactor } from '../spatial/motherboard-standard.js?v=20260817-pcieanchor10';

function finite(value,fallback=0){const n=Number(value);return Number.isFinite(n)?n:fallback;}

export function normalizeRamSlotCount(value,fallback=2){
  const n=Math.round(finite(value,0));
  if(n>0)return Math.max(1,Math.min(8,n));
  return Math.max(1,Math.min(8,Math.round(finite(fallback,2))||2));
}

export function motherboardRamSlotLocalPositions(dims={},formFactor='ITX',rawSlotCount=0){
  const ff=normalizeMotherboardFormFactor(formFactor);
  const fallback=defaultMotherboardRamSlotCount(ff);
  const slotCount=normalizeRamSlotCount(rawSlotCount,fallback);
  const boardL=finite(dims?.l,170)||170;
  const boardW=finite(dims?.w,170)||170;
  const landmarks=motherboardLandmarks({l:boardL,w:boardW},ff);

  // Physical UDIMM connector dimensions from the cleaned reference.
  const moduleLength=Math.min(133.35,Math.max(90,boardL-18));
  const moduleDepth=6;
  const referenceXs=Array.isArray(landmarks.ramSlotXs)?landmarks.ramSlotXs:[];
  const pitch=finite(landmarks.ramPitch,referenceXs.length>1?Math.abs(referenceXs[1]-referenceXs[0]):10)||10;

  let xs;
  if(referenceXs.length===slotCount){
    xs=[...referenceXs];
  }else{
    // Non-standard slot counts retain the measured bank centre and form-factor pitch. This
    // keeps 2-DIMM mATX / unusual boards coherent without inventing model-specific CAD data.
    const bankSpan=moduleDepth+Math.max(0,slotCount-1)*pitch;
    const halfSpan=bankSpan/2;
    const minCenterX=(-boardW/2)+halfSpan+4;
    const maxCenterX=(boardW/2)-halfSpan-4;
    const centerX=Math.max(minCenterX,Math.min(maxCenterX,landmarks.ramBank[0]));
    xs=Array.from({length:slotCount},(_,i)=>centerX+(i-(slotCount-1)/2)*pitch);
  }

  const y=(MOTHERBOARD_PCB_THICKNESS/2)+0.15;
  const centerZ=landmarks.ramBank[2];
  return xs.map((x,index)=>({
    index,x,y,z:centerZ,length:moduleLength,depth:moduleDepth,pitch,
    source:referenceXs.length===slotCount?'measured_form_factor_reference':'measured_bank_derived'
  }));
}

export function installedRamSlotIndices(rawSlotCount=2,moduleCount=2){
  const slotCount=normalizeRamSlotCount(rawSlotCount,2);
  const count=Math.max(1,Math.min(slotCount,Math.round(finite(moduleCount,2))||2));
  if(count>=slotCount)return Array.from({length:slotCount},(_,i)=>i);
  if(count===2 && slotCount===4)return [1,3]; // conventional A2/B2 visual default
  if(count===2 && slotCount===2)return [0,1];
  if(count===1)return [Math.min(slotCount-1,slotCount>=2?1:0)];
  const out=[];
  for(let i=0;i<count;i++){
    const idx=Math.round(((i+1)*(slotCount+1)/(count+1))-1);
    if(!out.includes(idx))out.push(Math.max(0,Math.min(slotCount-1,idx)));
  }
  for(let i=0;out.length<count&&i<slotCount;i++)if(!out.includes(i))out.push(i);
  return out.sort((a,b)=>a-b);
}

export function ramKitBoardPlacement(dims={},formFactor='ITX',rawSlotCount=0,moduleCount=2){
  const ff=normalizeMotherboardFormFactor(formFactor);
  const slots=motherboardRamSlotLocalPositions(dims,ff,rawSlotCount);
  const indices=installedRamSlotIndices(slots.length,moduleCount);
  const selected=indices.map(index=>slots[index]).filter(Boolean);
  if(!selected.length)return null;
  const anchor={
    x:selected.reduce((sum,p)=>sum+p.x,0)/selected.length,
    y:selected.reduce((sum,p)=>sum+p.y,0)/selected.length,
    z:selected.reduce((sum,p)=>sum+p.z,0)/selected.length
  };
  return {
    slotCount:slots.length,
    moduleCount:selected.length,
    indices,
    slots,
    selected,
    anchor,
    source:selected.every(p=>p.source==='measured_form_factor_reference')
      ? 'measured_form_factor_reference'
      : 'measured_bank_derived',
    offsets:selected.map(p=>({x:p.x-anchor.x,y:0,z:p.z-anchor.z,index:p.index}))
  };
}
